import { ChevronRight } from "lucide-react"
import { Link, matchPath, useLocation } from "react-router"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@components/ui/sidebar"
import { NavDropdown } from "./NavDropdown"
import type { NavItem } from "./navItems"

export const NavMain = ({ items }: { items: NavItem[] }) => {
  const { pathname } = useLocation()
  const { state, isMobile, setOpenMobile } = useSidebar()

  const isActive = (href: string) =>
    !!matchPath({ path: href, end: false }, pathname)
  // Navigating from the mobile sheet should reveal the new page
  const closeMobile = () => setOpenMobile(false)
  // Submenus can't expand inline while collapsed to icons, so they open as a dropdown instead
  const showSubmenusAsDropdown = state === "collapsed" && !isMobile

  return (
    <SidebarGroup>
      <SidebarMenu className="tw:m-0 tw:p-0">
        {items.map((item) => {
          if (!item.items) {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={{
                    children: item.title,
                    className:
                      "tw:bg-secondary tw:text-secondary-foreground tw:[&_svg]:bg-secondary tw:[&_svg]:fill-secondary",
                  }}
                  isActive={isActive(item.href)}
                >
                  <Link
                    to={item.href}
                    onClick={closeMobile}
                    className="tw:text-sidebar-foreground tw:no-underline"
                  >
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          const isGroupActive = item.items.some((subItem) =>
            isActive(subItem.href),
          )

          if (showSubmenusAsDropdown) {
            return (
              <NavDropdown
                key={item.title}
                item={item}
                isActive={isGroupActive}
              />
            )
          }

          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={isGroupActive}
              className="tw:group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton>
                    <item.icon />
                    <span>{item.title}</span>
                    <ChevronRight className="tw:ml-auto tw:transition-transform tw:duration-200 tw:group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className="tw:mb-0">
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isActive(subItem.href)}
                        >
                          <Link
                            to={subItem.href}
                            onClick={closeMobile}
                            className="tw:no-underline"
                          >
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
